import React, { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, Box } from '@material-ui/core';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  InfoOutlined as InfoIcon,
  PhoneIphone as PhoneIcon,
} from '@material-ui/icons';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useUser } from '../../../../../global/auth/useUser';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { useSmsCost } from '../../../../../common/sms/use-sms-cost';
import { VolunteerCommonProfileEmail } from '../../__generated__/VolunteerCommonProfileEmail';
import { VolunteerCommonProfileSms } from '../../__generated__/VolunteerCommonProfileSms';
import { SmsLogTypeParsed } from '../../VolunteerCommonProfileDialog';
import { EmailContent } from '../../../../../common/communications/EmailContent';
import { SmsThread } from '../../../../../common/communications/SmsThread';

interface CommunicationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  communication: VolunteerCommonProfileEmail | VolunteerCommonProfileSms | SmsLogTypeParsed | null;
  recipientProfileId?: string;
  recipientName?: string;
  onReplySent?: () => void;
}

interface SendSmsRequest {
  profileIds: string[];
  sentBy: string;
  message: string;
  senderName?: string | null;
  allowReplies?: boolean;
}

export const CommunicationDetailDialog = ({
  open,
  onClose,
  communication,
  recipientProfileId,
  recipientName,
  onReplySent,
}: CommunicationDetailDialogProps) => {
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const org = useOrgInfo();
  const { messageUnits, costPerUnit, formatCost, isDomestic, rateLabel } = useSmsCost();
  const sendSms = useEndpointFetch<SendSmsRequest>('/vm/comms/send-sms');
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSendReply = async () => {
    if (!replyText.trim() || !recipientProfileId) return;

    setIsSending(true);
    try {
      const response = await sendSms.run({
        profileIds: [recipientProfileId],
        sentBy: user.profileId,
        message: replyText.trim(),
        senderName: `${user.firstName} ${user.lastName}`,
        allowReplies: true,
      });

      if (response && response.ok) {
        setSnackbar({ message: 'Reply sent', variant: 'success', open: true });
        setReplyText('');
        if (onReplySent) onReplySent();
      } else {
        setSnackbar({ message: 'Failed to send reply', variant: 'error', open: true });
      }
    } catch (error) {
      setSnackbar({ message: 'An error occurred while sending the reply', variant: 'error', open: true });
    } finally {
      setIsSending(false);
    }
  };

  const renderEmailContent = (email: VolunteerCommonProfileEmail) => (
    <EmailContent
      subject={email.subject}
      from={email.sentBy || 'System'}
      to={email.toEmail}
      sentAt={email.sentAt}
      body={email.body}
    />
  );

  const renderReplyComposer = (sms: VolunteerCommonProfileSms) => {
    // The backend prepends the sender prefix, so it has to be counted here too
    const senderPrefix = `${user.firstName} ${user.lastName} at ${org?.name} says: `;
    const units = messageUnits(senderPrefix + replyText);
    const cost = units * costPerUnit;

    if (!sms.repliable) {
      return (
        <Box style={{ borderTop: '1px solid #e7e7e7', paddingTop: '16px' }}>
          <Box
            style={{
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '14px 16px',
            }}
          >
            <Box display='flex' alignItems='flex-start' style={{ gap: '10px' }}>
              <InfoIcon style={{ fontSize: '18px', color: '#646464', flexShrink: 0, marginTop: '1px' }} />
              <Box>
                <Typography style={{ fontSize: '14px', color: '#444444', fontWeight: 600 }}>
                  Replies are not available for this message
                </Typography>
                <Typography style={{ fontSize: '13px', color: '#666', marginTop: '4px', lineHeight: 1.5 }}>
                  It was sent from the Volaby sender name, which volunteers cannot reply to. Send a new message to start
                  a conversation {recipientName || 'they'} can answer.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    if (!recipientProfileId) return null;

    return (
      <Box style={{ borderTop: '1px solid #e7e7e7', paddingTop: '16px' }}>
        <Typography style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          Reply by SMS to {sms.contactNumber}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant='outlined'
          placeholder='Write a reply'
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          inputProps={{ maxLength: 1600 }}
        />
        <Box display='flex' alignItems='center' justifyContent='space-between' style={{ marginTop: '0.5rem' }}>
          <Typography style={{ fontSize: '12px', color: '#666' }}>
            {replyText.length} characters • {units} message unit{units !== 1 ? 's' : ''} • {formatCost(cost)}
            {!isDomestic && ` (${rateLabel.toLowerCase()})`}
          </Typography>
          <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
            <Button
              onClick={() => setReplyText('')}
              disabled={isSending || replyText.length === 0}
              style={{ textTransform: 'none', fontSize: '0.75rem', color: '#545454' }}
            >
              Clear
            </Button>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={handleSendReply}
              disabled={isSending || !replyText.trim()}
              style={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSmsContent = (sms: VolunteerCommonProfileSms) => {
    const replies = sms.replies || [];
    const conversationLabel = recipientName
      ? `Conversation with ${recipientName} • ${sms.contactNumber}`
      : `Conversation with ${sms.contactNumber}`;

    return (
      <Box>
        <Typography style={{ fontSize: '12px', color: '#666', marginBottom: '14px' }}>
          {sms.repliable ? conversationLabel : `Sent to ${recipientName || sms.contactNumber}`}
        </Typography>

        <SmsThread
          messages={[
            { id: sms.outboundSmsId, body: sms.body, inbound: false, at: sms.sentAt, author: 'Sent' },
            ...replies.map((reply) => ({
              id: reply.inboundSmsId,
              body: reply.body,
              inbound: true,
              at: reply.receivedAt,
              author: recipientName || 'Received',
            })),
          ]}
        />

        {renderReplyComposer(sms)}
      </Box>
    );
  };

  const renderSmsLogContent = (smsLog: SmsLogTypeParsed) => {
    // Parse the messageData to extract actual message content
    const getMessageContent = (messageData: string) => {
      try {
        // Check if it's already a plain text message
        if (typeof messageData === 'string' && !messageData.startsWith('O:') && !messageData.startsWith('{')) {
          return messageData;
        }

        // Try to parse as JSON first
        const parsed = JSON.parse(messageData);

        // Check for common content fields in the parsed object
        if (parsed.content) {
          return parsed.content;
        }
        if (parsed.message) {
          return parsed.message;
        }
        if (parsed.body) {
          return parsed.body;
        }
        if (parsed.text) {
          return parsed.text;
        }

        // If no standard content field found, return the original string
        return messageData;
      } catch (error) {
        // For PHP serialized data (starts with O:), try to extract content field
        if (typeof messageData === 'string' && messageData.includes('s:7:"content"')) {
          // Look for content field in PHP serialized format
          const contentMatch = messageData.match(/s:7:"content";s:\d+:"([^"]+)"/);
          if (contentMatch && contentMatch[1]) {
            return contentMatch[1];
          }
        }

        // If parsing fails, return the original string
        return messageData;
      }
    };

    const messageContent = smsLog.messageData ? getMessageContent(smsLog.messageData) : 'No message data available';

    // Parse the messageData for reply detection
    const isReply = smsLog.direction;
    let replyStatus = 'N/A';

    if (isReply && messageContent) {
      const messageText = messageContent.toLowerCase().trim();
      if (messageText === 'y' || messageText === 'yes') {
        replyStatus = 'Y';
      } else if (messageText === 'n' || messageText === 'no') {
        replyStatus = 'N';
      }
    }

    return (
      <Box>
        <SmsThread
          messages={[
            {
              id: smsLog.smsLogId,
              body: messageContent,
              inbound: isReply,
              at: smsLog.sentAt,
              author: isReply ? recipientName || 'Received' : 'Sent',
            },
          ]}
        />

        {isReply && (
          <Box
            style={{
              backgroundColor: '#f5f5f5',
              padding: '8px',
              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <Typography style={{ fontWeight: 600, marginBottom: '2px', fontSize: '12px' }}>Reply Status:</Typography>
            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: replyStatus === 'Y' ? '#4caf50' : replyStatus === 'N' ? '#f44336' : '#666',
              }}
            >
              {replyStatus}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    if (!communication) return null;

    if (communication.__typename === 'EMAIL_PostmarkOutboundEmailType') {
      return renderEmailContent(communication as VolunteerCommonProfileEmail);
    }

    if (communication.__typename === 'EMAIL_OutboundSmsType') {
      return renderSmsContent(communication as VolunteerCommonProfileSms);
    }

    return renderSmsLogContent(communication as SmsLogTypeParsed);
  };

  const getCommunicationTypeChip = () => {
    if (!communication) return null;

    if (communication.__typename === 'EMAIL_PostmarkOutboundEmailType') {
      return (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#d9d9d9',
            borderRadius: '3px',
            padding: '4px 8px',
            marginLeft: '8px',
          }}
        >
          <EmailIcon style={{ width: '10px', height: '10px', color: '#545454', marginRight: '4px' }} />
          <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>Email</Typography>
        </Box>
      );
    }

    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#d9d9d9',
          borderRadius: '3px',
          padding: '4px 8px',
          marginLeft: '8px',
        }}
      >
        <PhoneIcon style={{ width: '10px', height: '10px', color: '#545454', marginRight: '4px' }} />
        <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>SMS</Typography>
      </Box>
    );
  };

  const getDialogSize = () => {
    if (!communication) return 'sm';

    // Use medium size for emails to accommodate full content, small for SMS
    return communication.__typename === 'EMAIL_PostmarkOutboundEmailType' ? 'md' : 'sm';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={getDialogSize()}
      fullWidth
      PaperProps={{
        style: { borderRadius: '12px' },
      }}
    >
      <DialogTitle style={{ padding: '16px 20px 12px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' alignItems='center'>
            <Typography style={{ fontSize: '20px', fontWeight: 700 }}>Communication Details</Typography>
            {getCommunicationTypeChip()}
          </Box>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '8px 20px 20px' }}>{renderContent()}</DialogContent>
    </Dialog>
  );
};
